import { useCallback } from "react";
import type { Expense, ExpenseStatus, FundKey, FundMovement, Settings } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import {
  removeExpense as apiRemoveExpense,
  saveExpense,
  saveLoan,
  saveSettings,
} from "@/store/api";
import { uid } from "@/utils/format";

// Gastos operativos/compras y los cuatro fondos (reparto del 25%).
export const useFundActions = (ctx: StoreCtx) => {
  const { data, setData, run } = ctx;

  const addExpense = useCallback(
    (e: Expense) =>
      run(async () => {
        const saved = await saveExpense(e);
        setData((p) => ({ ...p, expenses: [saved, ...p.expenses] }));
      }),
    [run, setData]
  );

  const updateExpense = useCallback(
    (e: Expense) =>
      run(async () => {
        const saved = await saveExpense(e);
        setData((p) => ({
          ...p,
          expenses: p.expenses.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  const deleteExpense = useCallback(
    (id: string) =>
      run(async () => {
        await apiRemoveExpense(id);
        setData((p) => ({ ...p, expenses: p.expenses.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  // Transferencia manual de saldo entre fondos.
  const addFundTransfer = useCallback(
    (input: { fromFund: FundKey; toFund: FundKey; amount: number; note: string }) =>
      run(async () => {
        const movement: FundMovement = {
          id: uid("fm"),
          date: new Date().toISOString(),
          kind: "transferencia",
          fromFund: input.fromFund,
          toFund: input.toFund,
          amount: Math.max(0, Number(input.amount) || 0),
          note: input.note.trim(),
        };
        const next: Settings = {
          ...data.settings,
          fundMovements: [...data.settings.fundMovements, movement],
        };
        await saveSettings(next);
        setData((p) => ({ ...p, settings: next }));
      }),
    [run, data.settings, setData]
  );

  // Borra un movimiento de fondos (transferencia o inyección/devolución).
  const removeFundMovement = useCallback(
    (id: string) =>
      run(async () => {
        const next: Settings = {
          ...data.settings,
          fundMovements: data.settings.fundMovements.filter((m) => m.id !== id),
        };
        await saveSettings(next);
        setData((p) => ({ ...p, settings: next }));
      }),
    [run, data.settings, setData]
  );

  // Paga (o abona) una compra / gasto pendiente desde un fondo.
  const payExpenseFromFund = useCallback(
    (expenseId: string, fund: FundKey, amount: number) =>
      run(async () => {
        const exp = data.expenses.find((e) => e.id === expenseId);
        if (!exp) return;
        const pay = Math.max(0, Math.min(Number(amount) || 0, exp.amount - exp.paidAmount));
        if (pay <= 0) return;
        const newPaid = exp.paidAmount + pay;
        const status: ExpenseStatus = newPaid >= exp.amount ? "pagado" : "parcial";
        const saved = await saveExpense({ ...exp, paidAmount: newPaid, status, fund });
        setData((p) => ({
          ...p,
          expenses: p.expenses.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, data.expenses, setData]
  );

  // Paga una compra / gasto con un préstamo de Juank.unlimited. La inyección
  // entra como liquidez al Fondo Caja Menor y queda registrada como deuda.
  const payExpenseWithLoan = useCallback(
    (expenseId: string, amount: number) =>
      run(async () => {
        const exp = data.expenses.find((e) => e.id === expenseId);
        if (!exp) return;
        const pay = Math.max(0, Math.min(Number(amount) || 0, exp.amount - exp.paidAmount));
        if (pay <= 0) return;
        const now = new Date().toISOString();
        const loan = await saveLoan({
          id: "",
          date: now,
          concept: `Préstamo para pagar: ${exp.concept}`,
          value: pay,
          type: "Préstamo",
        });
        const movement: FundMovement = {
          id: uid("fm"),
          date: now,
          kind: "prestamo_juank",
          fromFund: null,
          toFund: "caja",
          amount: pay,
          note: `Préstamo de Juank para ${exp.concept}`,
        };
        const next: Settings = {
          ...data.settings,
          fundMovements: [...data.settings.fundMovements, movement],
        };
        await saveSettings(next);
        const newPaid = exp.paidAmount + pay;
        const status: ExpenseStatus = newPaid >= exp.amount ? "pagado" : "parcial";
        const saved = await saveExpense({ ...exp, paidAmount: newPaid, status, fund: "caja" });
        setData((p) => ({
          ...p,
          loans: [loan, ...p.loans],
          settings: next,
          expenses: p.expenses.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, data.expenses, data.settings, setData]
  );

  return {
    addExpense,
    updateExpense,
    deleteExpense,
    addFundTransfer,
    removeFundMovement,
    payExpenseFromFund,
    payExpenseWithLoan,
  };
};